import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { haptic } from '../telegram';
import type { DiagnosisDto, PlantDto, TreatmentResearch } from '../types';
import { Card, Empty, ErrorBanner, Loader, formatDate } from '../components/common';

interface Props {
  spaceId: string;
  presetPlantId: string | null;
  onDone: () => void;
}

const URGENCY_LABELS: Record<string, string> = { low: 'низкая', medium: 'средняя', high: 'высокая' };
const STATUS_LABELS: Record<string, string> = {
  draft: 'черновик',
  analyzing: 'анализ',
  awaiting_answers: 'нужны ответы',
  open: 'не завершён',
  monitoring: 'наблюдение',
  resolved: 'решено',
  worsened: 'ухудшение',
  failed: 'ошибка',
};

export function Diagnoses({ spaceId, presetPlantId, onDone }: Props) {
  const [items, setItems] = useState<DiagnosisDto[]>([]);
  const [plants, setPlants] = useState<PlantDto[]>([]);
  const [plantId, setPlantId] = useState<string | null>(presetPlantId);
  const [symptoms, setSymptoms] = useState('');
  const [active, setActive] = useState<DiagnosisDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      const [list, plantList] = await Promise.all([api.diagnoses(spaceId), api.plants(spaceId)]);
      setItems(list);
      setPlants(plantList);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [spaceId]);

  /** Фото необязательно: по одному описанию диагноз тоже ставится, но осторожнее. */
  async function startDiagnosis(file?: File) {
    if (!file && !symptoms.trim()) {
      setError('Опишите, что происходит, или загрузите фото');
      return;
    }
    setBusy(true);
    setError(null);
    // Новый запуск — прежний результат убираем, чтобы не путать ответы к разным описаниям.
    setActive(null);
    try {
      const created = await api.createDiagnosis(spaceId, plantId, symptoms.trim() || null);
      if (file) await api.uploadDiagnosisImage(created.id, file);
      const analyzed = await api.analyzeDiagnosis(created.id);
      setActive(analyzed);
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setError(e instanceof Error ? e.message : 'Не удалось выполнить диагностику');
    } finally {
      setBusy(false);
    }
  }

  async function answer(questionKey: string, value: string) {
    if (!active) return;
    setBusy(true);
    try {
      setActive(await api.answerDiagnosis(active.id, questionKey, value));
    } finally {
      setBusy(false);
    }
  }

  /** Повторить анализ той же записи — без новой записи в истории. */
  async function retry(id: string) {
    setBusy(true);
    setError(null);
    try {
      setActive(await api.analyzeDiagnosis(id));
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setError(e instanceof Error ? e.message : 'Не удалось выполнить диагностику');
    } finally {
      setBusy(false);
    }
  }

  async function outcome(id: string, value: 'better' | 'same' | 'worse') {
    setBusy(true);
    try {
      await api.diagnosisOutcome(id, value);
      await load();
      onDone();
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Loader />;

  const result = active?.result ?? null;
  const pending = active?.questions.filter((q) => !q.answerText) ?? [];
  // Модель не ответила (сбой AI) — запись осталась незавершённой: говорим об этом явно.
  const unfinished = active !== null && !result && !pending.length;

  return (
    <div>
      <h1>Диагностика</h1>

      {error ? <ErrorBanner message={error} /> : null}

      <Card>
        <div className="title">Новая диагностика</div>
        <div className="hint mt">
          Фото отправляется AI-провайдеру. Результат — рекомендация, а не гарантированный диагноз.
        </div>

        <div className="field mt">
          <label>Растение</label>
          <select value={plantId ?? ''} onChange={(e) => setPlantId(e.target.value || null)}>
            <option value="">Не из коллекции</option>
            {plants.map((plant) => (
              <option key={plant.id} value={plant.id}>
                {plant.displayName}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Что беспокоит</label>
          <textarea
            rows={3}
            value={symptoms}
            onChange={(e) => setSymptoms(e.target.value)}
            placeholder="Например: листья желтеют и опадают снизу, на обороте белый налёт"
          />
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void startDiagnosis(file);
            e.target.value = '';
          }}
        />
        <div className="actions">
          <button className="btn" disabled={busy || !symptoms.trim()} onClick={() => void startDiagnosis()}>
            {busy ? 'Анализирую… до минуты' : '🔍 Разобрать по описанию'}
          </button>
          <button className="btn secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
            📷 С фото
          </button>
        </div>
        <div className="hint mt">
          Можно без фото — по описанию. С фото диагноз точнее. Время года учитываю: осенью многие
          растения желтеют и сбрасывают листья — это нормально.
        </div>
      </Card>

      {pending.length ? (
        <Card>
          <div className="title">Нужно уточнение</div>
          <div className="hint mt">{pending[0].questionText}</div>
          <div className="actions">
            {['Да', 'Нет', 'Не знаю'].map((value) => (
              <button
                key={value}
                className="btn secondary"
                disabled={busy}
                onClick={() => answer(pending[0].questionKey, value)}
              >
                {value}
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      {unfinished && active ? (
        <Card className="overdue">
          <div className="title">⚠️ Анализ не завершился</div>
          <div className="hint mt">
            Модель не ответила с первого раза — такое бывает. Повторите: это та же диагностика,
            новой записи в истории не будет.
          </div>
          <div className="actions">
            <button className="btn" disabled={busy} onClick={() => void retry(active.id)}>
              {busy ? 'Анализирую… до минуты' : '🔁 Повторить'}
            </button>
          </div>
        </Card>
      ) : null}

      {result ? (
        <Card>
          <div className="title">Результат</div>
          <div className="mt">{result.summary}</div>

          {result.likely_causes.length ? (
            <>
              <h2>Наиболее вероятно</h2>
              {result.likely_causes.map((cause) => (
                <div key={cause.name} className="mt">
                  <b>{cause.name}</b> <span className="hint">{Math.round(cause.confidence * 100)}%</span>
                  {cause.explanation ? <div className="hint">{cause.explanation}</div> : null}
                </div>
              ))}
            </>
          ) : null}

          {result.do_now.length ? (
            <>
              <h2>Сделать сейчас</h2>
              <ul>
                {result.do_now.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          ) : null}

          {result.check.length ? (
            <>
              <h2>Проверить</h2>
              <ul>
                {result.check.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          ) : null}

          {result.treatment_options.length ? (
            <>
              <h2>Лечение и коррекция ухода</h2>
              {result.treatment_options.map((option) => (
                <div key={option.title} className="mt">
                  <b>{option.title}</b>
                  <ul>
                    {option.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ul>
                  {option.substance_note ? <div className="hint">{option.substance_note}</div> : null}
                </div>
              ))}
            </>
          ) : null}

          {result.treatment_research ? <TreatmentBlock research={result.treatment_research} /> : null}

          <div className="hint mt">
            Через {result.follow_up.days} дн.: {result.follow_up.what_to_check}
          </div>
        </Card>
      ) : null}

      <h2>История</h2>
      {!items.length ? <Empty title="Диагностик пока не было" /> : null}
      {items.map((item) => (
        <Card key={item.id}>
          <div className="row between">
            <span className="title">{item.plantName ?? 'Без растения'}</span>
            <span className="badge">{STATUS_LABELS[item.status] ?? item.status}</span>
          </div>
          <div className="hint">
            {formatDate(item.createdAt)}
            {item.urgency ? ` · срочность: ${URGENCY_LABELS[item.urgency] ?? item.urgency}` : ''}
            {item.confidence != null ? ` · уверенность ${Math.round(item.confidence * 100)}%` : ''}
          </div>
          {item.summary ? <div className="mt">{item.summary}</div> : null}
          {item.result?.treatment_research ? (
            <TreatmentBlock research={item.result.treatment_research} compact />
          ) : null}
          {item.images.length ? (
            <div className="photo-grid mt">
              {item.images.map((url) => (
                <img key={url} src={url} alt="фото диагностики" />
              ))}
            </div>
          ) : null}
          {(item.status === 'open' || item.status === 'analyzing') && !item.result ? (
            <div className="actions">
              <button className="btn secondary" disabled={busy} onClick={() => void retry(item.id)}>
                🔁 Повторить анализ
              </button>
            </div>
          ) : null}
          {item.status === 'monitoring' ? (
            <div className="actions">
              <button className="btn secondary" disabled={busy} onClick={() => outcome(item.id, 'better')}>
                🙂 Лучше
              </button>
              <button className="btn secondary" disabled={busy} onClick={() => outcome(item.id, 'same')}>
                😐 Без изменений
              </button>
              <button className="btn secondary" disabled={busy} onClick={() => outcome(item.id, 'worse')}>
                🙁 Хуже
              </button>
            </div>
          ) : null}
        </Card>
      ))}
    </div>
  );
}

/** Модель иногда пишет «null» строкой — это отсутствие данных. */
function clean(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  return ['null', 'none', 'n/a', '-', '—'].includes(text.toLowerCase()) ? '' : text;
}

const PRODUCT_TYPES: Record<string, string> = {
  fungicide: 'фунгицид',
  insecticide: 'инсектицид',
  acaricide: 'акарицид',
  bactericide: 'бактерицид',
  biological: 'биопрепарат',
};

function TreatmentBlock({ research, compact = false }: { research: TreatmentResearch; compact?: boolean }) {
  const products = compact ? research.products.slice(0, 2) : research.products;
  if (!products.length && !research.non_chemical.length) return null;
  return (
    <div className="mt">
      {products.length ? (
        <>
          <h2>Препараты</h2>
          <div className="hint">Подобраны по открытым источникам{research.targets.length ? `: ${research.targets.join(', ')}` : ''}</div>
          {products.map((product) => (
            <div key={product.name} className="mt">
              <b>{product.name}</b>
              {[PRODUCT_TYPES[product.product_type], product.active_ingredient].filter(Boolean).length ? (
                <span className="hint">
                  {' '}
                  — {[PRODUCT_TYPES[product.product_type], product.active_ingredient].filter(Boolean).join(', ')}
                </span>
              ) : null}
              {!compact ? (
                <ul>
                  <li>Дозировка: {clean(product.dosage) || 'смотрите инструкцию на упаковке'}</li>
                  {clean(product.preparation) ? <li>Как развести: {product.preparation}</li> : null}
                  {clean(product.application) ? <li>Как применять: {product.application}</li> : null}
                  {clean(product.timing) ? <li>Когда: {product.timing}</li> : null}
                  {product.interval_days ? (
                    <li>
                      {product.repeat_count ? `${product.repeat_count} обработки, ` : 'Повтор '}через{' '}
                      {product.interval_days} дн.
                    </li>
                  ) : null}
                  {product.suitable_indoors === false ? <li>Не для помещений — обрабатывайте на улице</li> : null}
                </ul>
              ) : null}
              {!compact && clean(product.precautions) ? <div className="hint">{product.precautions}</div> : null}
            </div>
          ))}
        </>
      ) : null}
      {!compact && research.non_chemical.length ? (
        <>
          <h2>Без химии</h2>
          <ul>
            {research.non_chemical.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      ) : null}
      {!compact && research.sources.length ? (
        <div className="hint mt">
          Источники:{' '}
          {research.sources.map((source, index) => (
            <span key={source.url}>
              {index ? ', ' : ''}
              <a href={source.url} target="_blank" rel="noreferrer">
                {source.title || index + 1}
              </a>
            </span>
          ))}
        </div>
      ) : null}
      {!compact ? (
        <div className="hint mt">Перед применением прочитайте инструкцию на упаковке.</div>
      ) : null}
    </div>
  );
}

